import React, { useState, useEffect, useCallback } from "react";
import { Save, Plus, Trash2, GripVertical } from "lucide-react";
import { supabase } from "../../lib/supabase";

interface ModulesSectionProps {
  courseId?: string;
  initialData: any[];
}

export default function ModulesSection({
  courseId,
  initialData,
}: ModulesSectionProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [modules, setModules] = useState<any[]>([]);

  // Initialize modules from initialData
  useEffect(() => {
    if (initialData) {
      setModules(
        initialData.map((module: any) => ({
          title: module.title,
          description: module.description,
          duration: module.duration,
          order_number: module.order_number,
          topics:
            module.module_topics?.map((topic: any) => ({
              topic_text: topic.topic_text,
              order_number: topic.order_number,
            })) || [],
        })),
      );
    }
  }, [initialData]);

  // Check for changes
  const checkForChanges = useCallback(() => {
    if (!initialData) return false;

    const hasChanges =
      modules.length !== initialData.length ||
      modules.some((module, index) => {
        const initialModule = initialData[index];
        if (!initialModule) return true;

        const topicsChanged =
          module.topics.length !== initialModule.module_topics?.length ||
          module.topics.some((topic: any, topicIndex: number) => {
            const initialTopic = initialModule.module_topics?.[topicIndex];
            return (
              !initialTopic ||
              topic.topic_text !== initialTopic.topic_text ||
              topic.order_number !== initialTopic.order_number
            );
          });

        return (
          module.title !== initialModule.title ||
          module.description !== initialModule.description ||
          module.duration !== initialModule.duration ||
          module.order_number !== initialModule.order_number ||
          topicsChanged
        );
      });

    setHasUnsavedChanges(hasChanges);
  }, [modules, initialData]);

  useEffect(() => {
    checkForChanges();
  }, [modules, checkForChanges]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      if (!courseId) return;

      // Delete existing modules
      await supabase.from("course_modules").delete().eq("course_id", courseId);

      // Insert new modules and their topics
      for (const module of modules) {
        const { topics, ...moduleData } = module;

        // Insert module
        const { data: newModule, error: moduleError } = await supabase
          .from("course_modules")
          .insert([{ ...moduleData, course_id: courseId }])
          .select()
          .single();

        if (moduleError) throw moduleError;

        // Insert topics for this module
        if (topics && topics.length > 0 && newModule) {
          const topicsWithModuleId = topics.map((topic: any) => ({
            ...topic,
            module_id: newModule.id,
          }));

          const { error: topicsError } = await supabase
            .from("module_topics")
            .insert(topicsWithModuleId);

          if (topicsError) throw topicsError;
        }
      }

      // Update initialData reference
      Object.assign(initialData, modules);
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error("Error saving modules:", err);
      setError(err instanceof Error ? err.message : "Failed to save modules");
    } finally {
      setSaving(false);
    }
  };

  const addModule = () => {
    setModules((prev) => [
      ...prev,
      {
        title: "",
        description: "",
        duration: "",
        order_number: prev.length + 1,
        topics: [],
      },
    ]);
  };

  const updateModule = (index: number, field: string, value: any) => {
    setModules((prev) =>
      prev.map((module, i) =>
        i === index ? { ...module, [field]: value } : module,
      ),
    );
  };

  const removeModule = (index: number) => {
    setModules((prev) => {
      const newModules = prev.filter((_, i) => i !== index);
      // Update order numbers
      return newModules.map((module, i) => ({
        ...module,
        order_number: i + 1,
      }));
    });
  };

  const addTopic = (moduleIndex: number) => {
    setModules((prev) =>
      prev.map((module, i) =>
        i === moduleIndex
          ? {
              ...module,
              topics: [
                ...module.topics,
                {
                  topic_text: "",
                  order_number: module.topics.length + 1,
                },
              ],
            }
          : module,
      ),
    );
  };

  const updateTopic = (
    moduleIndex: number,
    topicIndex: number,
    text: string,
  ) => {
    setModules((prev) =>
      prev.map((module, i) =>
        i === moduleIndex
          ? {
              ...module,
              topics: module.topics.map((topic: any, j: number) =>
                j === topicIndex ? { ...topic, topic_text: text } : topic,
              ),
            }
          : module,
      ),
    );
  };

  const removeTopic = (moduleIndex: number, topicIndex: number) => {
    setModules((prev) =>
      prev.map((module, i) =>
        i === moduleIndex
          ? {
              ...module,
              topics: module.topics
                .filter((_: any, j: number) => j !== topicIndex)
                .map((topic: any, j: number) => ({
                  ...topic,
                  order_number: j + 1,
                })),
            }
          : module,
      ),
    );
  };

  return (
    <div className="bg-white shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-medium text-gray-900">Course Modules</h2>
          <button
            onClick={handleSave}
            disabled={saving || !hasUnsavedChanges}
            className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium ${
              hasUnsavedChanges
                ? "text-white bg-[#F98B3D] hover:bg-[#e07a2c]"
                : "text-gray-500 bg-gray-100"
            } disabled:opacity-50 transition-colors duration-200`}
          >
            <Save className="h-5 w-5 mr-2" />
            {saving ? "Saving..." : "Save Modules"}
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          {modules.map((module, moduleIndex) => (
            <div
              key={moduleIndex}
              className="border border-gray-200 rounded-lg p-4"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <GripVertical className="h-5 w-5 text-gray-400 cursor-move" />
                  <span className="ml-2 text-gray-700">
                    Module {moduleIndex + 1}
                  </span>
                </div>
                <button
                  onClick={() => removeModule(moduleIndex)}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Title
                  </label>
                  <input
                    type="text"
                    value={module.title}
                    onChange={(e) =>
                      updateModule(moduleIndex, "title", e.target.value)
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <textarea
                    value={module.description}
                    onChange={(e) =>
                      updateModule(moduleIndex, "description", e.target.value)
                    }
                    rows={2}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Duration
                  </label>
                  <input
                    type="text"
                    value={module.duration}
                    onChange={(e) =>
                      updateModule(moduleIndex, "duration", e.target.value)
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                    placeholder="e.g., 2 hours"
                  />
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Topics
                    </label>
                    <button
                      type="button"
                      onClick={() => addTopic(moduleIndex)}
                      className="text-sm text-[#F98B3D] hover:text-[#e07a2c]"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="space-y-2">
                    {module.topics.map((topic: any, topicIndex: number) => (
                      <div key={topicIndex} className="flex items-center gap-2">
                        <GripVertical className="h-4 w-4 text-gray-400 cursor-move" />
                        <input
                          type="text"
                          value={topic.topic_text}
                          onChange={(e) =>
                            updateTopic(moduleIndex, topicIndex, e.target.value)
                          }
                          className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                          placeholder="Enter topic"
                        />
                        <button
                          onClick={() => removeTopic(moduleIndex, topicIndex)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={addModule}
          className="mt-4 w-full py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <Plus className="h-5 w-5 inline-block mr-2" />
          Add Module
        </button>
      </div>
    </div>
  );
}
